/**
 * UltimatumScreen2.tsx – Second screen of Ultimatum Game (recipient's perspective, Unified Design)
 * Last updated: 2025-08-14
 * Unified translation, design & BlueprintJS compatibility
 */

import React, { useEffect, useState } from "react";
import { Slider, FormGroup, Icon } from "@blueprintjs/core";
import kinogutscheinImg from "./assets/Ultimatum_Kinogutschein.png";
import { ScreenProps } from "../../SequenceController";
import { useLanguage } from "../../../../context/LanguageContext";
import { Ultimatum } from "../../../../translations/experiments/Ultimatum";
import { colors, commonStyles } from "../../../../components/colors";

type ScreenClick = {
    minAcceptableAmount: number;
    millisecondsSinceFirstRender: number;
};

type ScreenResult = {
    selections: Array<ScreenClick>;
    usedHelp: boolean;
};

export const Screen8InitialResult: ScreenResult = {
    selections: [],
    usedHelp: false
};

export const UltimatumScreen2 = (props: ScreenProps) => {
    const [firstRenderTimestamp, setFirstRenderTimestamp] = useState(0);
    const [slider, setSlider] = useState(10); // Start in the middle
    const { setCanContinue, setResult } = props;
    const { language } = useLanguage();
    const t = Ultimatum[language].screens.screen2;

    useEffect(() => {
        setFirstRenderTimestamp(Date.now());
    }, [setResult]);

    useEffect(() => {
        setCanContinue(true);
        setResult((result: ScreenResult) => {
            result.selections = [];
            result.selections.push({
                minAcceptableAmount: slider,
                millisecondsSinceFirstRender: Date.now() - firstRenderTimestamp
            });
            return result;
        });
    }, [slider, setCanContinue, setResult, firstRenderTimestamp]);

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <div style={commonStyles.card}>
                {/* Headline */}
                <h2 style={{
                    color: colors.brillantBlue,
                    margin: "0 0 24px 0",
                    fontSize: 28,
                    fontWeight: 700,
                    textAlign: "center"
                }}>
                    {t.title}
                </h2>
                {/* Paragraphs (story) */}
                <div style={{
                    ...commonStyles.paragraph,
                    textAlign: "justify",
                    textJustify: "inter-word"
                }}>
                    {t.paragraphs.map((p: string, i: number) => (
                        <p key={i} style={{ margin: i === 0 ? "0 0 14px 0" : "18px 0 0 0" }}>
                            {p}
                        </p>
                    ))}
                </div>
                {/* Orange info box with icon and message */}
                <div style={{
                    backgroundColor: colors.ocker,
                    border: `1px solid ${colors.ockerLight}`,
                    padding: "14px 20px",
                    borderRadius: 8,
                    margin: "32px 0 24px 0",
                    fontSize: 16,
                    fontWeight: 500,
                    color: colors.white,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    boxShadow: "0 2px 8px 0 rgba(215,125,0,0.14)"
                }}>
                    <Icon icon="time" style={{ marginRight: 9, fontSize: 18, marginTop: 2 }} />
                    <span>
                        {/* Info box text: use t.noConsult if available, else fallback */}
                        {t.noConsult ||
                            (language === 'en'
                                ? "There is no time to consult or discuss with your partner – you must decide immediately."
                                : "Du hast keine Zeit, dich mit deinem Partner oder deiner Partnerin abzusprechen – du musst sofort entscheiden.")}
                    </span>
                </div>
                {/* Image */}
                <div style={{ textAlign: "center", margin: "40px 0 0 0" }}>
                    <img
                        src={kinogutscheinImg}
                        alt={t.imgAlt}
                        style={{
                            maxWidth: 340,
                            width: "100%",
                            height: "auto",
                            borderRadius: 16,
                            boxShadow: "0 6px 20px 0 rgba(0,0,0,0.09)"
                        }}
                    />
                </div>
                {/* Centered Question above slider */}
                <div style={{ textAlign: "center", margin: "40px 0 18px 0" }}>
                    <span style={{
                        color: colors.brillantBlue,
                        fontSize: 22,
                        fontWeight: 600,
                        textAlign: "center"
                    }}>{t.sliderLabel}</span>
                </div>
                {/* Slider */}
                <FormGroup
                    label={undefined}
                    labelFor="slider-input"
                    style={{
                        marginTop: 0,
                        marginBottom: 30
                    }}
                >
                    <div style={{ maxWidth: 540, margin: "0 auto" }}>
                        <Slider
                            min={0}
                            max={20}
                            stepSize={1}
                            labelStepSize={5}
                            value={slider}
                            onChange={setSlider}
                            labelRenderer={num => `${num} €`}
                            intent={undefined}
                            className="no-track-slider"
                        />
                    </div>
                </FormGroup>
                {/* Slider Result */}
                <p style={{
                    textAlign: "center",
                    marginTop: 20,
                    color: colors.textDark,
                    fontSize: 16
                }}>
                    <span
                        dangerouslySetInnerHTML={{
                            __html: t.sliderResultText(slider, 20 - slider)
                        }}
                    />
                </p>
            </div>
        </div>
    );
};